// Types for the Hierarchical Table Component

// Defines a handling tier with its GSLA values
export interface HandlingTierData {
    id: string;
    name: string; // e.g., "Default", "rush"
    currentGSLA: number | undefined;
    recommendedGSLA: number | undefined;
    publishDateEpochMillis: number | undefined;
    recommendationDateEpochMillis: number | undefined;
    overrideValue?: number;
    isDefault: boolean; // Whether this is the default handling tier
    action?: string; // NoAction, Recommended, or a reason code
}

// Defines a production capability with its default and additional handling tiers
export interface ProductionCapabilityData {
    id: string;
    name: string; // e.g., "Stow", "Pick", "Pack"
    defaultHandlingTier: HandlingTierData;
    additionalHandlingTiers: HandlingTierData[];
}

// The main data structure for the hierarchical table
export interface HierarchicalTableData {
    warehouseId: string;
    productionCapabilities: ProductionCapabilityData[];
}

// Structure for pending changes to be saved
export interface PendingChange {
    productionCapability: string;
    handlingTier: string;
    currentGSLA?: number;
    newGSLA?: number;
    action: string;
    isDefault: boolean;
}

// Row state based on actions
export enum RowState {
    UNCHANGED = 'unchanged',
    RECOMMENDATION_ACCEPTED = 'recommendation-accepted',
    OVERRIDE_PENDING = 'override-pending',
    DELETE_PENDING = 'delete-pending',
    PARENT_DELETE_PENDING = 'parent-delete-pending', // When parent PC is being deleted
    HIGHER_RECOMMENDED = 'higher-recommended',
    LOWER_RECOMMENDED = 'lower-recommended'
}

// Action types for pending changes
export enum ActionType {
    NO_ACTION = 'NoAction',
    RECOMMENDED = 'Recommended',
    DELETE = 'Delete',
    HVE = 'HVE',
    NEW_FC = 'NewFC',
    MHE_CHANGE = 'MHEChange',
    DEA_PERFORMANCE = 'DEAPerformance',
    OUTLIERS = 'Outliers'
}

// Props for the main hierarchical table component
export interface HierarchicalTableProps {
    data: HierarchicalTableData;
    onSave: (changes: PendingChange[]) => Promise<void>;
    userCanEdit: boolean;
    initialPendingChanges?: Record<string, Record<string, PendingChange>>;
    warehouseId: string;
    availableTiers?: string[];
    isPSSD?: boolean;
}

// Props for the production capability row component
export interface PCRowProps {
    pc: ProductionCapabilityData;
    isExpanded: boolean;
    onToggleExpand: (pcId: string) => void;
    pendingChanges: Map<string, PendingChange>; // Map of handlingTier IDs to pending changes
    onValueChange: (pcId: string, tierId: string, value: number, action: string) => void;
    onDelete?: (pcId: string) => void; // Handler for PC deletion
    onAddHandlingTier?: (pcId: string, tierName: string) => void; // Handler for adding handling tiers
    availableTiers?: string[]; // List of available tier names to add
    userCanEdit: boolean;
    warehouseId: string;
    isPSSD?: boolean;
}

// Props for the handling tier row component
export interface HTRowProps {
    tier: HandlingTierData;
    pcId: string;
    pcName: string;
    pendingChange?: PendingChange;
    parentPcDeletePending?: boolean;  // Whether the parent PC has a pending deletion
    onValueChange: (pcId: string, tierId: string, value: number, action: string) => void;
    onDelete?: (pcId: string, tierId: string) => void;
    userCanEdit: boolean;
    warehouseId: string;
}

// Props for the add handling tier component
export interface AddHandlingTierProps {
    pcId: string;
    availableTiers: string[];
    onAdd: (pcId: string, tierName: string) => void;
    userCanEdit: boolean;
    initialAdding?: boolean; // Control initial adding state externally
}

// Props for the save changes footer component
export interface SaveChangesFooterProps {
    pendingChanges: Map<string, Map<string, PendingChange>>; // Map of pcId -> Map of tierId -> PendingChange
    onSave: () => Promise<void>;
    userCanEdit: boolean;
}

// Props for the action buttons component
export interface ActionButtonsProps {
    tier: HandlingTierData;
    pcId: string;
    onValueChange: (pcId: string, tierId: string, value: number, action: string) => void;
    onDelete?: (pcId: string, tierId: string) => void;
    userCanEdit: boolean;
}
